import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import {
  collection, getDocs, doc, updateDoc,
  addDoc, deleteDoc, getDoc, setDoc
} from "firebase/firestore";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Analytics from "./Analytics";

const ARTICLE_CATEGORIES = ["History", "Social Life", "Politics", "GK", "Science", "Economy", "Culture", "Sports"];
const CERT_TYPES = ["Membership Certificate", "Participation Certificate", "Volunteer Certificate", "Achievement Certificate", "Appreciation Certificate"];
const PRESIDENT_EMAIL = "honestpro77@gmail.com";

export default function Admin({ onBack }) {
  const [members, setMembers] = useState([]);
  const [polls, setPolls] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [articles, setArticles] = useState([]);
  const [activeTab, setActiveTab] = useState("members");
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showLoginHistory, setShowLoginHistory] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCity, setFilterCity] = useState("all");

  const [certModal, setCertModal] = useState(null);
  const [certType, setCertType] = useState(CERT_TYPES[0]);
  const [certReason, setCertReason] = useState("");
  const [certGenerating, setCertGenerating] = useState(false);

  const [annTitle, setAnnTitle] = useState("");
  const [annMessage, setAnnMessage] = useState("");
  const [annPosting, setAnnPosting] = useState(false);
  const [progForm, setProgForm] = useState({ title: "", date: "", location: "", desc: "", status: "upcoming" });
  const [progSaving, setProgSaving] = useState(false);
  const [editingProg, setEditingProg] = useState(null);
  const [artForm, setArtForm] = useState({ title: "", desc: "", content: "", category: "History", readTime: "3 min" });
  const [artSaving, setArtSaving] = useState(false);
  const [editingArt, setEditingArt] = useState(null);

  const isPresident = auth.currentUser?.email === PRESIDENT_EMAIL;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const [mS, pS, aS, prS, arS] = await Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "polls")),
      getDocs(collection(db, "announcements")),
      getDocs(collection(db, "programs")),
      getDocs(collection(db, "knowledgeArticles")),
    ]);
    setMembers(mS.docs.map(d => ({ id: d.id, ...d.data() })));
    setPolls(pS.docs.map(d => ({ id: d.id, ...d.data() })));
    setAnnouncements(aS.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt));
    setPrograms(prS.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt));
    setArticles(arS.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt));
    setLoading(false);
  };

  const filteredMembers = members.filter(m => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q ||
      (m.naam || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.username || "").toLowerCase().includes(q) ||
      (m.membershipId || "").toLowerCase().includes(q) ||
      (m.phone || "").includes(q);
    const matchRole = filterRole === "all" || m.role === filterRole;
    const matchStatus = filterStatus === "all" || m.membershipStatus === filterStatus;
    const matchCity = filterCity === "all" || (m.city || "").toLowerCase() === filterCity.toLowerCase();
    return matchSearch && matchRole && matchStatus && matchCity;
  });

  const uniqueCities = [...new Set(members.map(m => m.city).filter(Boolean))];

  const updateRole = async (uid, role) => {
    if (!window.confirm(`Role change karein to "${role}"?`)) return;
    await updateDoc(doc(db, "users", uid), { role });
    await addDoc(collection(db, "notifications", uid, "items"), { text: `Your role has been updated to: ${role}`, createdAt: Date.now(), unread: true });
    fetchData();
  };

  const updateMembership = async (uid, status) => {
    await updateDoc(doc(db, "users", uid), { membershipStatus: status }); fetchData();
  };

  const closePoll = async (pollId) => {
    await updateDoc(doc(db, "polls", pollId), { status: "closed", expiresAt: Date.now() }); fetchData();
  };

  const toggleAdminAccess = async (uid, currentAccess, naam) => {
    const newAccess = !currentAccess;
    if (!window.confirm(newAccess ? `"${naam}" ko Admin access dein?` : `"${naam}" ka Admin access hatayein?`)) return;
    await updateDoc(doc(db, "users", uid), { adminAccess: newAccess });
    await addDoc(collection(db, "notifications", uid, "items"), {
      text: newAccess ? `Aapko Admin Panel ka access diya gaya hai!` : `Aapka Admin Panel access hataya gaya hai.`,
      createdAt: Date.now(), unread: true,
    });
    fetchData();
  };
  const deleteMember = async (uid, naam) => {
  if (!window.confirm(`"${naam}" ka account permanently delete karein? Yeh undo nahi ho sakta!`)) return;
  await deleteDoc(doc(db, "users", uid));
  alert(`${naam} ka account delete ho gaya.`);
  fetchData();
  };
  const migrateIds = async () => {
    if (!window.confirm("Saare members ko GBS-0001 format mein ID assign karein?")) return;
    const snap = await getDocs(collection(db, "users"));
    const noId = snap.docs.filter(d => !d.data().membershipId);
    if (noId.length === 0) { alert("Sab members ki ID already set hai!"); return; }
    let count = 0;
    try {
      const counterSnap = await getDoc(doc(db, "counters", "memberCount"));
      count = counterSnap.exists() ? (counterSnap.data().count || 0) : 0;
    } catch {}
    for (const d of noId) {
      count++;
      await updateDoc(doc(db, "users", d.id), { membershipId: `GBS-${String(count).padStart(4, "0")}` });
    }
    await setDoc(doc(db, "counters", "memberCount"), { count });
    alert(`${noId.length} members ko ID assign ho gayi!`);
    fetchData();
  };

  const migrateUsernames = async () => {
    if (!window.confirm("Existing members ke usernames migrate karein?")) return;
    const snap = await getDocs(collection(db, "users"));
    let count = 0;
    for (const d of snap.docs) {
      const data = d.data();
      if (data.username && data.email) {
        await setDoc(doc(db, "usernames", data.username), { email: data.email, uid: d.id });
        count++;
      }
    }
    alert(`${count} usernames migrate ho gaye!`);
  };

  const exportCSV = () => {
    const rows = [
      ["Member ID", "Naam", "Username", "Email", "Phone", "Role", "Status", "State", "District", "City", "Area", "Joined"],
      ...members.map(m => [
        m.membershipId || "—", m.naam || "—", m.username || "—", m.email || "—",
        m.phone || "—", m.role || "member", m.membershipStatus || "pending",
        m.state || "—", m.district || "—", m.city || "—", m.area || "—",
        m.createdAt ? new Date(m.createdAt).toLocaleDateString("en-IN") : "—"
      ])
    ];
    const csvContent = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `GBS_Members_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.csv`;
    link.click();
  };

  const exportExcel = () => {
    const data = members.map(m => ({
      "Member ID": m.membershipId || "—", "Naam": m.naam || "—", "Username": m.username || "—",
      "Email": m.email || "—", "Phone": m.phone || "—", "Role": m.role || "member",
      "Status": m.membershipStatus || "pending", "State": m.state || "—", "District": m.district || "—",
      "City": m.city || "—", "Area": m.area || "—", "Fee Paid": m.membershipFee ? `₹${m.membershipFee}` : "—",
      "Joined": m.createdAt ? new Date(m.createdAt).toLocaleDateString("en-IN") : "—",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Members");
    XLSX.writeFile(wb, `GBS_Members_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.xlsx`);
  };

  const exportPDF = () => {
    const pdf = new jsPDF({ orientation: "landscape" });
    pdf.setFontSize(14);
    pdf.text("Global Bahuudeshiya Sanstha — Member List", 14, 15);
    pdf.setFontSize(10);
    pdf.text(`Generated: ${new Date().toLocaleDateString("en-IN")} | Total: ${members.length}`, 14, 22);
    const columns = ["ID", "Naam", "Email", "Phone", "Role", "Status", "City", "Joined"];
    const rows = members.map(m => [
      m.membershipId || "—", m.naam || "—", m.email || "—", m.phone || "—",
      m.role || "member", m.membershipStatus || "pending", m.city || "—",
      m.createdAt ? new Date(m.createdAt).toLocaleDateString("en-IN") : "—",
    ]);
    pdf.autoTable({
      head: [columns], body: rows, startY: 28,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [13, 27, 62], textColor: 255 },
      alternateRowStyles: { fillColor: [247, 247, 249] },
    });
    pdf.save(`GBS_Members_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.pdf`);
  };

  const postAnnouncement = async () => {
    if (!annTitle.trim() || !annMessage.trim()) return;
    setAnnPosting(true);
    await addDoc(collection(db, "announcements"), { title: annTitle.trim(), message: annMessage.trim(), createdAt: Date.now() });
    setAnnTitle(""); setAnnMessage(""); await fetchData(); setAnnPosting(false);
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete karein?")) return;
    await deleteDoc(doc(db, "announcements", id)); fetchData();
  };

  const saveProgram = async () => {
    if (!progForm.title.trim() || !progForm.date.trim()) return;
    setProgSaving(true);
    if (editingProg) await updateDoc(doc(db, "programs", editingProg), { ...progForm });
    else await addDoc(collection(db, "programs"), { ...progForm, createdAt: Date.now() });
    setProgForm({ title: "", date: "", location: "", desc: "", status: "upcoming" });
    setEditingProg(null); await fetchData(); setProgSaving(false);
  };

  const deleteProgram = async (id) => {
    if (!window.confirm("Delete karein?")) return;
    await deleteDoc(doc(db, "programs", id)); fetchData();
  };

  const startEditProg = (p) => {
    setProgForm({ title: p.title, date: p.date, location: p.location || "", desc: p.desc || "", status: p.status });
    setEditingProg(p.id);
  };

  const saveArticle = async () => {
    if (!artForm.title.trim() || !artForm.desc.trim()) return;
    setArtSaving(true);
    if (editingArt) await updateDoc(doc(db, "knowledgeArticles", editingArt), { ...artForm });
    else await addDoc(collection(db, "knowledgeArticles"), { ...artForm, createdAt: Date.now() });
    setArtForm({ title: "", desc: "", content: "", category: "History", readTime: "3 min" });
    setEditingArt(null); await fetchData(); setArtSaving(false);
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete karein?")) return;
    await deleteDoc(doc(db, "knowledgeArticles", id)); fetchData();
  };

  const startEditArt = (a) => {
    setArtForm({ title: a.title, desc: a.desc, content: a.content || "", category: a.category, readTime: a.readTime || "3 min" });
    setEditingArt(a.id);
  };

  const generateCertificate = async () => {
    if (!certModal) return;
    setCertGenerating(true);
    const canvas = document.createElement("canvas");
    canvas.width = 1200; canvas.height = 850;
    const ctx = canvas.getContext("2d");
    drawCertificateBase(ctx, canvas.width, canvas.height);
    ctx.fillStyle = "#374151"; ctx.font = "20px Georgia"; ctx.textAlign = "center";
    ctx.fillText("This is to certify that", 600, 330);
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 52px Georgia";
    ctx.fillText(certModal.naam || "—", 600, 405);
    const nameWidth = ctx.measureText(certModal.naam || "—").width;
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(600 - nameWidth / 2, 415); ctx.lineTo(600 + nameWidth / 2, 415); ctx.stroke();
    ctx.fillStyle = "#6B7280"; ctx.font = "18px Arial";
    ctx.fillText(`${(certModal.role || "Member").replace(/_/g, " ").toUpperCase()} | ${certModal.city || ""}, ${certModal.state || ""}`, 600, 450);
    drawCertificateFooter(ctx, certModal, certType, certReason);
    await addDoc(collection(db, "notifications", certModal.id, "items"), {
      text: `Aapko "${certType}" award kiya gaya hai!`, createdAt: Date.now(), unread: true,
    });
    await addDoc(collection(db, "certificates", certModal.id, "items"), {
      type: certType, reason: certReason.trim(), issuedAt: Date.now(), issuedBy: "Admin",
    });
    downloadCanvas(canvas, `Certificate_${certModal.naam?.replace(/\s/g, "_")}`);
    setCertGenerating(false); setCertModal(null); setCertType(CERT_TYPES[0]); setCertReason("");
  };

  const downloadBlankCertificate = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1200; canvas.height = 850;
    const ctx = canvas.getContext("2d");
    drawCertificateBase(ctx, canvas.width, canvas.height);
    ctx.fillStyle = "#374151"; ctx.font = "20px Georgia"; ctx.textAlign = "center";
    ctx.fillText("This is to certify that", 600, 330);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(250, 415); ctx.lineTo(950, 415); ctx.stroke();
    ctx.fillStyle = "#9CA3AF"; ctx.font = "16px Arial"; ctx.fillText("(Name)", 600, 410);
    ctx.strokeStyle = "#9CA3AF"; ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.moveTo(200, 490 + i * 36); ctx.lineTo(1000, 490 + i * 36); ctx.stroke(); }
    drawCertificateFooter(ctx, null, "____________________", "");
    downloadCanvas(canvas, "Blank_Certificate_GBS");
  };

  const downloadLetterhead = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1000; canvas.height = 1400;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FDFAF4"; ctx.fillRect(0, 0, 1000, 1400);
    ctx.fillStyle = "#0D1B3E"; ctx.fillRect(0, 0, 1000, 130);
    ctx.fillStyle = "#0D1B3E"; ctx.fillRect(0, 130, 1000, 5);
    ctx.fillStyle = "#FFFFFF"; ctx.font = "bold 28px Arial"; ctx.textAlign = "center";
    ctx.fillText("GLOBAL BAHUUDESHIYA SANSTHA", 500, 60);
    ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "italic 15px Georgia";
    ctx.fillText('"Seva aur Vikas"', 500, 90);
    ctx.fillStyle = "rgba(255,255,255,0.45)"; ctx.font = "12px Arial";
    ctx.fillText("Nagpur, Maharashtra | Darpan ID: MH/2025/0496085 | Reg. No.: F-31059 | Est. 2013", 500, 115);
    const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 13px Arial"; ctx.textAlign = "left";
    ctx.fillText("DATE:", 80, 175); ctx.fillStyle = "#374151"; ctx.font = "13px Arial"; ctx.fillText(today, 160, 175);
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 13px Arial"; ctx.fillText("REF NO:", 80, 200);
    ctx.fillStyle = "#374151"; ctx.font = "13px Arial"; ctx.fillText("GBS / _______ / 2026-27", 160, 200);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 1.5; ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(80, 215); ctx.lineTo(920, 215); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 14px Arial"; ctx.fillText("TO,", 80, 255);
    for (let i = 0; i < 3; i++) { ctx.strokeStyle = "#C8C8C8"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(80, 290 + i * 35); ctx.lineTo(920, 290 + i * 35); ctx.stroke(); }
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 14px Arial"; ctx.fillText("SUBJECT:", 80, 400);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(210, 402); ctx.lineTo(920, 402); ctx.stroke();
    ctx.fillStyle = "#374151"; ctx.font = "14px Georgia"; ctx.fillText("Respected Sir / Madam,", 80, 450);
    ctx.strokeStyle = "#E0D5C5"; ctx.lineWidth = 1;
    for (let i = 0; i < 18; i++) { ctx.beginPath(); ctx.moveTo(80, 490 + i * 42); ctx.lineTo(920, 490 + i * 42); ctx.stroke(); }
    ctx.fillStyle = "#374151"; ctx.font = "14px Georgia"; ctx.fillText("Yours Faithfully,", 80, 1260);
    [[120, 1290, "PRESIDENT"], [390, 1290, "TREASURER"], [660, 1290, "SECRETARY"]].forEach(([x, y, title]) => {
      ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 1.5; ctx.strokeRect(x, y, 200, 50);
      ctx.fillStyle = "#F9FAFB"; ctx.fillRect(x + 1, y + 1, 198, 48);
      ctx.fillStyle = "#9CA3AF"; ctx.font = "11px Arial"; ctx.textAlign = "center";
      ctx.fillText("(Signature)", x + 100, y + 30);
      ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 12px Arial"; ctx.fillText(title, x + 100, y + 62);
    });
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(500, 1330, 45, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 11px Arial"; ctx.textAlign = "center";
    ctx.fillText("OFFICIAL", 500, 1323); ctx.fillText("SEAL", 500, 1338);
    ctx.textAlign = "left"; ctx.fillStyle = "#0D1B3E"; ctx.fillRect(0, 1380, 1000, 4);
    downloadCanvas(canvas, "Letterhead_GBS");
  };

  const drawCertificateBase = (ctx, w, h) => {
    ctx.fillStyle = "#FDFAF5"; ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 12; ctx.strokeRect(20, 20, w - 40, h - 40);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 1; ctx.strokeRect(36, 36, w - 72, h - 72);
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 14px Arial"; ctx.textAlign = "center";
    ctx.fillText("GLOBAL BAHUUDESHIYA SANSTHA", w/2, 100);
    ctx.fillStyle = "#6B7280"; ctx.font = "13px Arial";
    ctx.fillText("Established 2013 | Nagpur, Maharashtra", w/2, 125);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(100, 145); ctx.lineTo(w-100, 145); ctx.stroke();
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 48px Georgia";
    ctx.fillText("CERTIFICATE", w/2, 220);
  };

  const drawCertificateFooter = (ctx, member, type, reason) => {
    ctx.fillStyle = "#374151"; ctx.font = "20px Georgia"; ctx.textAlign = "center";
    ctx.fillText(`of  ${type.replace("Certificate", "").trim() || type}`, 600, 265);
    const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    if (reason) {
      const words = reason.split(" "); let line = ""; let y = 500; ctx.font = "18px Georgia";
      for (let word of words) {
        const test = line + word + " ";
        if (ctx.measureText(test).width > 900 && line !== "") { ctx.fillText(line, 600, y); line = word + " "; y += 32; } else line = test;
      }
      ctx.fillText(line, 600, y);
    }
    ctx.fillStyle = "#374151"; ctx.font = "16px Arial"; ctx.fillText(`Date: ${today}`, 600, 660);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(200, 730); ctx.lineTo(450, 730); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(750, 730); ctx.lineTo(1000, 730); ctx.stroke();
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 15px Arial";
    ctx.fillText("President", 325, 752); ctx.fillText("Secretary", 875, 752);
    ctx.fillStyle = "#6B7280"; ctx.font = "13px Arial";
    ctx.fillText("Global Bahuudeshiya Sanstha", 325, 770);
    ctx.fillText("Global Bahuudeshiya Sanstha", 875, 770);
    ctx.strokeStyle = "#0D1B3E"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(600, 740, 45, 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = "#0D1B3E"; ctx.font = "bold 11px Arial";
    ctx.fillText("OFFICIAL", 600, 733); ctx.fillText("SEAL", 600, 750);
    const certId = member ? `GBS-CERT-${member.id?.slice(0,6).toUpperCase()}-${Date.now().toString().slice(-4)}` : `GBS-CERT-BLANK`;
    ctx.fillStyle = "#9CA3AF"; ctx.font = "12px Arial";
    ctx.fillText(`Certificate ID: ${certId}`, 600, 820);
  };

  const downloadCanvas = (canvas, filename) => {
    const link = document.createElement("a");
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL("image/png"); link.click();
  };

  const totalFee = members.reduce((s, m) => s + (m.membershipFee || 0), 0);
  const totalDeposit = members.reduce((s, m) => s + (m.securityDeposit || 0), 0);
  const activeMembers = members.filter(m => m.membershipStatus === "active").length;
  const pendingMembers = members.filter(m => m.membershipStatus === "pending").length;

  const NOW = Date.now();
  const tabs = [
    { id: "members", label: "Members" }, { id: "polls", label: "Polls" },
    { id: "announcements", label: "News" }, { id: "programs", label: "Events" },
    { id: "knowledge", label: "Knowledge" }, { id: "finance", label: "Finance" },
    { id: "activity", label: "Activity" }, { id: "analytics", label: "Analytics" },
  ];

  return (
    <div style={S.container}>
      {certModal && (
        <div style={S.overlay} onClick={() => setCertModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={S.modalHeading}>Certificate Issue Karein</p>
              <button onClick={() => setCertModal(null)} style={S.closeBtn}>×</button>
            </div>
            <div style={S.modalSummary}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#0D1B3E" }}>{certModal.naam}</p>
              <p style={{ margin: 0, fontSize: 12, color: "#9CA3AF" }}>{certModal.role} · {certModal.city}</p>
            </div>
            <label style={S.label}>Certificate Type</label>
            <select style={S.input} value={certType} onChange={e => setCertType(e.target.value)}>
              {CERT_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <label style={S.label}>Reason (optional)</label>
            <textarea style={{ ...S.input, minHeight: 70, resize: "vertical" }}
              placeholder="e.g. for outstanding contribution..." value={certReason} onChange={e => setCertReason(e.target.value)} />
            <button onClick={generateCertificate} disabled={certGenerating} style={S.primaryBtnFull}>
              {certGenerating ? "Generating..." : "Generate & Download"}
            </button>
          </div>
        </div>
      )}

      {selectedMember && (
        <div style={S.overlay} onClick={() => { setSelectedMember(null); setShowLoginHistory(false); }}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div style={S.avatarCircle}>{(selectedMember.naam || "?")[0].toUpperCase()}</div>
              <button onClick={() => setSelectedMember(null)} style={S.closeBtn}>×</button>
            </div>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <p style={S.modalName}>{selectedMember.naam || "—"}</p>
              {selectedMember.username && <p style={{ margin: "0 0 8px", fontSize: 12, color: "#9CA3AF" }}>@{selectedMember.username}</p>}
              <span style={S.roleBadge}>{selectedMember.role || "member"}</span>
            </div>
            <div style={S.infoGrid}>
              {[
                { label: "Email", value: selectedMember.email },
                { label: "Member ID", value: selectedMember.membershipId || "—" },
                { label: "Phone", value: selectedMember.phone },
                { label: "State", value: selectedMember.state },
                { label: "City", value: selectedMember.city },
                { label: "Area", value: selectedMember.area },
                { label: "Membership", value: selectedMember.membershipStatus || "pending" },
                { label: "Admin Access", value: selectedMember.adminAccess ? "Yes" : "No" },
                { label: "Last Seen", value: selectedMember.lastSeen ? new Date(selectedMember.lastSeen).toLocaleString("en-IN") : "—" },
              ].map(({ label, value }) => (
                <div key={label} style={S.infoRow}>
                  <span style={S.infoLabel}>{label}</span>
                  <span style={S.infoValue}>{value || "—"}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowLoginHistory(p => !p)} style={S.historyToggle}>
              Login History {showLoginHistory ? "▲" : "▼"}
            </button>
            {showLoginHistory && (
              <div style={S.historyList}>
                {(!selectedMember.loginHistory || selectedMember.loginHistory.length === 0)
                  ? <p style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center" }}>Koi history nahi</p>
                  : [...(selectedMember.loginHistory || [])].reverse().slice(0, 15).map((h, i) => (
                    <div key={i} style={S.historyItem}><span style={{ fontSize: 11, color: "#0D1B3E" }}>{new Date(h.timestamp || h.loginAt).toLocaleString("en-IN")}</span></div>
                  ))}
              </div>
            )}
            <button onClick={() => { setSelectedMember(null); setCertModal(selectedMember); }} style={S.primaryBtnFull}>
              Certificate Issue Karein
            </button>
          </div>
        </div>
      )}

      <div style={S.header}>
        <div>
          <p style={S.headerSub}>Admin Panel</p>
          <h3 style={S.headerTitle}>Global Bahuudeshiya Sanstha</h3>
        </div>
        <button onClick={onBack} style={S.backBtn}>Back</button>
      </div>

      <div style={S.content}>
        <div style={S.quickRow}>
          <button onClick={downloadBlankCertificate} style={S.quickBtn}>Blank Cert</button>
          <button onClick={downloadLetterhead} style={S.quickBtn}>Letterhead</button>
          {isPresident && <button onClick={migrateIds} style={S.quickBtn}>IDs</button>}
          {isPresident && <button onClick={migrateUsernames} style={S.quickBtn}>Usernames</button>}
        </div>

        <div style={S.exportRow}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Export:</p>
          <button onClick={exportCSV} style={S.exportBtn}>CSV</button>
          <button onClick={exportExcel} style={S.exportBtn}>Excel</button>
          <button onClick={exportPDF} style={S.exportBtn}>PDF</button>
        </div>

        <div style={S.tabRow}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ ...S.tabBtn, background: activeTab === t.id ? "#0D1B3E" : "#fff", color: activeTab === t.id ? "#fff" : "#0D1B3E" }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && <p style={{ textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>Loading...</p>}

        {!loading && activeTab === "members" && (
          <div>
            <div style={S.filterBox}>
              <input style={{ ...S.input, marginBottom: 8 }} placeholder="Naam, email, username, ID search karein..."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              <div style={{ display: "flex", gap: 8 }}>
                <select style={{ ...S.input, marginBottom: 0, flex: 1 }} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
                  <option value="all">All Roles</option>
                  {["member","volunteer","area_head","secretary","president","treasurer","admin"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select style={{ ...S.input, marginBottom: 0, flex: 1 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                </select>
                <select style={{ ...S.input, marginBottom: 0, flex: 1 }} value={filterCity} onChange={e => setFilterCity(e.target.value)}>
                  <option value="all">All Cities</option>
                  {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <p style={S.count}>
              Showing: {filteredMembers.length} / {members.length}
              {(searchQuery || filterRole !== "all" || filterStatus !== "all" || filterCity !== "all") &&
                <span onClick={() => { setSearchQuery(""); setFilterRole("all"); setFilterStatus("all"); setFilterCity("all"); }} style={S.clearFilter}>Clear</span>}
            </p>

            {filteredMembers.map(m => (
              <div key={m.id} style={S.card}>
                <div style={S.cardTop}>
                  <div>
                    <p style={S.memberName}>{m.naam}</p>
                    {m.username && <p style={S.memberUsername}>@{m.username}</p>}
                    <p style={S.memberEmail}>{m.email}</p>
                    <p style={S.memberLocation}>{m.city}, {m.state}</p>
                    <p style={S.memberId}>{m.membershipId || "ID pending"}</p>
                    {m.adminAccess && <span style={S.adminTag}>Admin</span>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                    <span style={{ ...S.statusBadge, color: m.membershipStatus === "active" ? "#16A34A" : "#D97706" }}>
                      {m.membershipStatus === "active" ? "Active" : "Pending"}
                    </span>
                    <button onClick={() => { setSelectedMember(m); setShowLoginHistory(false); }} style={S.viewBtn}>View</button>
                    <button onClick={() => setCertModal(m)} style={S.viewBtn}>Cert</button>
                    {isPresident && m.email !== PRESIDENT_EMAIL && (
                      <button onClick={() => toggleAdminAccess(m.id, m.adminAccess, m.naam)} style={S.viewBtn}>
                        {m.adminAccess ? "Access OFF" : "Access ON"}
                      </button>
                    )}
                    {isPresident && m.email !== PRESIDENT_EMAIL && (
                      <button onClick={() => deleteMember(m.id, m.naam)} style={{ ...S.viewBtn, color: "#DC2626" }}>Delete</button>
                    )}
                  </div>
                </div>
                <div style={S.actionRow}>
                  <select value={m.role} onChange={e => updateRole(m.id, e.target.value)} style={S.select}>
                    {["member","volunteer","area_head","secretary","president","treasurer",
                      ...(isPresident ? ["admin"] : [])
                    ].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button onClick={() => updateMembership(m.id, m.membershipStatus === "active" ? "pending" : "active")} style={S.actionBtn}>
                    {m.membershipStatus === "active" ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && activeTab === "polls" && (
          <div>
            <p style={S.count}>Total: {polls.length}</p>
            {polls.map(p => (
              <div key={p.id} style={S.card}>
                <p style={S.memberName}>{p.question}</p>
                <p style={S.memberEmail}>Votes: {p.totalVotes || 0}</p>
                <div style={S.actionRow}>
                  <span style={{ ...S.statusBadge, color: Date.now() > p.expiresAt ? "#16A34A" : "#D97706" }}>
                    {Date.now() > p.expiresAt ? "Closed" : "Live"}
                  </span>
                  {Date.now() < p.expiresAt && <button onClick={() => closePoll(p.id)} style={S.actionBtn}>Close</button>}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && activeTab === "announcements" && (
          <div>
            <div style={S.formCard}>
              <p style={S.formTitle}>Naya Announcement</p>
              <input style={S.input} placeholder="Title" value={annTitle} onChange={e => setAnnTitle(e.target.value)} />
              <textarea style={{ ...S.input, minHeight: 70, resize: "vertical" }} placeholder="Message" value={annMessage} onChange={e => setAnnMessage(e.target.value)} />
              <button onClick={postAnnouncement} disabled={annPosting || !annTitle.trim() || !annMessage.trim()}
                style={{ ...S.primaryBtnFull, opacity: (!annTitle.trim() || !annMessage.trim()) ? 0.5 : 1 }}>
                {annPosting ? "Posting..." : "Post Karein"}
              </button>
            </div>
            {announcements.map(a => (
              <div key={a.id} style={S.card}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div style={{ flex: 1 }}>
                    <p style={S.memberName}>{a.title}</p>
                    <p style={{ margin: "4px 0 6px", fontSize: 13, color: "#374151" }}>{a.message}</p>
                    <p style={{ margin: 0, fontSize: 11, color: "#9CA3AF" }}>{new Date(a.createdAt).toLocaleString("en-IN")}</p>
                  </div>
                  <button onClick={() => deleteAnnouncement(a.id)} style={{ ...S.actionBtn, marginLeft: 8 }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && activeTab === "programs" && (
          <div>
            <div style={S.formCard}>
              <p style={S.formTitle}>{editingProg ? "Edit Program" : "Naya Program"}</p>
              <input style={S.input} placeholder="Title" value={progForm.title} onChange={e => setProgForm(p => ({ ...p, title: e.target.value }))} />
              <input style={S.input} placeholder="Date" value={progForm.date} onChange={e => setProgForm(p => ({ ...p, date: e.target.value }))} />
              <input style={S.input} placeholder="Location" value={progForm.location} onChange={e => setProgForm(p => ({ ...p, location: e.target.value }))} />
              <textarea style={{ ...S.input, minHeight: 70, resize: "vertical" }} placeholder="Description" value={progForm.desc} onChange={e => setProgForm(p => ({ ...p, desc: e.target.value }))} />
              <select style={S.input} value={progForm.status} onChange={e => setProgForm(p => ({ ...p, status: e.target.value }))}>
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
              </select>
              <div style={{ display: "flex", gap: 8 }}>
                {editingProg && <button onClick={() => { setEditingProg(null); setProgForm({ title: "", date: "", location: "", desc: "", status: "upcoming" }); }} style={S.secondaryBtn}>Cancel</button>}
                <button onClick={saveProgram} disabled={progSaving || !progForm.title.trim() || !progForm.date.trim()}
                  style={{ ...S.primaryBtn, opacity: (!progForm.title.trim() || !progForm.date.trim()) ? 0.5 : 1 }}>
                  {progSaving ? "Saving..." : editingProg ? "Update" : "Add"}
                </button>
              </div>
            </div>
            <p style={S.count}>Total: {programs.length}</p>
            {programs.map(p => (
              <div key={p.id} style={S.card}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div style={{ flex: 1 }}>
                    <p style={S.memberName}>{p.title}</p>
                    <p style={S.memberEmail}>{p.date}{p.location ? ` · ${p.location}` : ""}</p>
                  </div>
                  <span style={{ ...S.statusBadge, color: p.status === "upcoming" ? "#D97706" : "#16A34A" }}>{p.status}</span>
                </div>
                <div style={{ ...S.actionRow, marginTop: 10 }}>
                  <button onClick={() => startEditProg(p)} style={S.actionBtn}>Edit</button>
                  <button onClick={() => deleteProgram(p.id)} style={S.actionBtn}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && activeTab === "knowledge" && (
          <div>
            <div style={S.formCard}>
              <p style={S.formTitle}>{editingArt ? "Edit Article" : "Naya Article"}</p>
              <input style={S.input} placeholder="Title" value={artForm.title} onChange={e => setArtForm(p => ({ ...p, title: e.target.value }))} />
              <textarea style={{ ...S.input, minHeight: 70, resize: "vertical" }} placeholder="Short description" value={artForm.desc} onChange={e => setArtForm(p => ({ ...p, desc: e.target.value }))} />
              <textarea style={{ ...S.input, minHeight: 100, resize: "vertical" }} placeholder="Full content (optional)" value={artForm.content} onChange={e => setArtForm(p => ({ ...p, content: e.target.value }))} />
              <select style={S.input} value={artForm.category} onChange={e => setArtForm(p => ({ ...p, category: e.target.value }))}>
                {ARTICLE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select style={S.input} value={artForm.readTime} onChange={e => setArtForm(p => ({ ...p, readTime: e.target.value }))}>
                {["2 min","3 min","4 min","5 min","7 min","10 min"].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <div style={{ display: "flex", gap: 8 }}>
                {editingArt && <button onClick={() => { setEditingArt(null); setArtForm({ title: "", desc: "", content: "", category: "History", readTime: "3 min" }); }} style={S.secondaryBtn}>Cancel</button>}
                <button onClick={saveArticle} disabled={artSaving || !artForm.title.trim() || !artForm.desc.trim()}
                  style={{ ...S.primaryBtn, opacity: (!artForm.title.trim() || !artForm.desc.trim()) ? 0.5 : 1 }}>
                  {artSaving ? "Saving..." : editingArt ? "Update" : "Add Article"}
                </button>
              </div>
            </div>
            <p style={S.count}>Total: {articles.length} articles</p>
            {articles.map(a => (
              <div key={a.id} style={S.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <p style={S.memberName}>{a.title}</p>
                    <p style={{ margin: "2px 0 4px", fontSize: 11, color: "#9CA3AF" }}>{a.category} · {a.readTime}</p>
                    <p style={{ margin: 0, fontSize: 12, color: "#6B7280" }}>{a.desc?.slice(0, 80)}...</p>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginLeft: 8 }}>
                    <button onClick={() => startEditArt(a)} style={S.actionBtn}>Edit</button>
                    <button onClick={() => deleteArticle(a.id)} style={S.actionBtn}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && activeTab === "finance" && (
          <div>
            <div style={S.financeGrid}>
              {[
                { label: "Active Members", value: activeMembers },
                { label: "Pending Members", value: pendingMembers },
                { label: "Total Fee Collected", value: `₹${totalFee.toLocaleString()}` },
                { label: "Total Deposits", value: `₹${totalDeposit.toLocaleString()}` },
              ].map(s => (
                <div key={s.label} style={S.financeCard}>
                  <p style={S.financeLabel}>{s.label}</p>
                  <p style={S.financeValue}>{s.value}</p>
                </div>
              ))}
            </div>

            <p style={S.formTitle}>Member Fee Status</p>
            {members.map(m => (
              <div key={m.id} style={S.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={S.memberName}>{m.naam}</p>
                    <p style={{ margin: "2px 0", fontSize: 11, color: "#9CA3AF" }}>{m.membershipId || "—"} · {m.email}</p>
                    <p style={{ margin: 0, fontSize: 12, color: "#374151" }}>
                      Fee: ₹{m.membershipFee || 0} · Deposit: ₹{m.securityDeposit || 0} · Refunded: {m.depositRefunded ? "Yes" : "No"}
                    </p>
                  </div>
                  <span style={{ ...S.statusBadge, color: m.membershipStatus === "active" ? "#16A34A" : "#D97706" }}>
                    {m.membershipStatus === "active" ? "Active" : "Pending"}
                  </span>
                </div>
                {isPresident && (
                  <div style={{ ...S.actionRow, marginTop: 8 }}>
                    <button onClick={async () => { await updateDoc(doc(db, "users", m.id), { depositRefunded: !m.depositRefunded }); fetchData(); }} style={S.actionBtn}>
                      {m.depositRefunded ? "Cancel Refund" : "Mark Refunded"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && activeTab === "activity" && (
          <div>
            <div style={S.activityGrid}>
              {[
                { label: "Online", count: members.filter(m => m.lastSeen && NOW - m.lastSeen < 5 * 60 * 1000).length },
                { label: "Today", count: members.filter(m => m.lastSeen && NOW - m.lastSeen < 24 * 60 * 60 * 1000).length },
                { label: "Weekly", count: members.filter(m => m.lastSeen && NOW - m.lastSeen < 7 * 24 * 60 * 60 * 1000).length },
                { label: "Inactive", count: members.filter(m => !m.lastSeen || NOW - m.lastSeen >= 7 * 24 * 60 * 60 * 1000).length },
              ].map(s => (
                <div key={s.label} style={S.financeCard}>
                  <p style={S.financeLabel}>{s.label}</p>
                  <p style={S.financeValue}>{s.count}</p>
                </div>
              ))}
            </div>
            {[...members].sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0)).map(m => {
              const status = !m.lastSeen ? "Inactive" : NOW - m.lastSeen < 5 * 60 * 1000 ? "Online" : NOW - m.lastSeen < 24 * 60 * 60 * 1000 ? "Today" : NOW - m.lastSeen < 7 * 24 * 60 * 60 * 1000 ? "Weekly" : "Inactive";
              return (
                <div key={m.id} style={S.card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <p style={S.memberName}>{m.naam}</p>
                      <p style={{ margin: 0, fontSize: 11, color: "#9CA3AF" }}>{m.lastSeen ? new Date(m.lastSeen).toLocaleString("en-IN") : "Never"}</p>
                    </div>
                    <span style={S.statusBadge}>{status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && activeTab === "analytics" && (
          <Analytics members={members} polls={polls} />
        )}
      </div>
    </div>
  );
}

const S = {
  container: { minHeight: "100vh", background: "#F7F7F9", fontFamily: "-apple-system, 'Segoe UI', sans-serif" },
  overlay: { position: "fixed", inset: 0, background: "rgba(13,27,62,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal: { background: "#fff", borderRadius: 20, width: "100%", maxWidth: 420, maxHeight: "90vh", overflowY: "auto", padding: 20, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
  modalHeading: { margin: 0, fontSize: 15, fontWeight: 700, color: "#0D1B3E" },
  modalSummary: { background: "#F7F7F9", borderRadius: 10, padding: "10px 14px", marginBottom: 14 },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 },
  avatarCircle: { width: 52, height: 52, borderRadius: "50%", background: "#0D1B3E", color: "#fff", fontSize: 20, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
  closeBtn: { background: "#F7F7F9", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 16, color: "#6B7280" },
  modalName: { margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "#0D1B3E" },
  roleBadge: { fontSize: 10, fontWeight: 700, color: "#0D1B3E", background: "#F7F7F9", padding: "3px 10px", borderRadius: 6, textTransform: "uppercase", letterSpacing: 0.5 },
  infoGrid: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 },
  infoRow: { display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#F7F7F9", borderRadius: 8 },
  infoLabel: { fontSize: 11, color: "#9CA3AF", fontWeight: 500 },
  infoValue: { fontSize: 12, color: "#0D1B3E", fontWeight: 600, textAlign: "right", maxWidth: "55%" },
  historyToggle: { width: "100%", padding: 9, background: "#F7F7F9", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#0D1B3E", cursor: "pointer", marginBottom: 8 },
  historyList: { background: "#F7F7F9", borderRadius: 8, padding: "8px 10px", maxHeight: 160, overflowY: "auto" },
  historyItem: { padding: "5px 0", borderBottom: "1px solid #ECECEE" },
  header: { background: "linear-gradient(135deg, #0D1B3E, #1A2F5A)", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerSub: { color: "rgba(255,255,255,0.45)", fontSize: 11, margin: 0 },
  headerTitle: { color: "#fff", margin: 0, fontSize: 14, fontWeight: 700 },
  backBtn: { background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 10, padding: "8px 14px", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  content: { padding: 16 },
  quickRow: { display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" },
  quickBtn: { flex: 1, padding: "9px 6px", background: "#0D1B3E", color: "#fff", border: "none", borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: "pointer" },
  exportRow: { display: "flex", gap: 8, alignItems: "center", marginBottom: 12, background: "#fff", borderRadius: 12, padding: "10px 14px", border: "1px solid #ECECEE" },
  exportBtn: { padding: "6px 12px", background: "#F7F7F9", color: "#0D1B3E", border: "none", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer" },
  filterBox: { background: "#fff", borderRadius: 12, padding: 12, marginBottom: 12, border: "1px solid #ECECEE" },
  tabRow: { display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", overflowX: "auto" },
  tabBtn: { padding: "8px 12px", border: "1px solid #ECECEE", borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  count: { fontSize: 12, color: "#9CA3AF", marginBottom: 12 },
  clearFilter: { marginLeft: 8, color: "#0D1B3E", cursor: "pointer", fontSize: 11, fontWeight: 600 },
  card: { background: "#fff", borderRadius: 14, padding: 16, marginBottom: 10, border: "1px solid #ECECEE" },
  cardTop: { display: "flex", justifyContent: "space-between", marginBottom: 10 },
  memberName: { margin: "0 0 2px", fontSize: 14, fontWeight: 600, color: "#0D1B3E" },
  memberUsername: { margin: 0, fontSize: 11, color: "#9CA3AF" },
  memberEmail: { margin: "0 0 2px", fontSize: 12, color: "#9CA3AF" },
  memberLocation: { margin: 0, fontSize: 12, color: "#9CA3AF" },
  memberId: { margin: "2px 0 0", fontSize: 11, color: "#0D1B3E", fontWeight: 700 },
  adminTag: { fontSize: 10, background: "#F7F7F9", color: "#0D1B3E", padding: "2px 8px", borderRadius: 6, fontWeight: 700, marginTop: 4, display: "inline-block" },
  statusBadge: { fontSize: 11, fontWeight: 600, padding: "3px 0" },
  actionRow: { display: "flex", gap: 8, alignItems: "center" },
  select: { flex: 1, padding: "8px 10px", borderRadius: 8, border: "1px solid #ECECEE", fontSize: 12, background: "#fff" },
  actionBtn: { padding: "8px 14px", border: "1px solid #ECECEE", background: "#fff", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#0D1B3E" },
  viewBtn: { background: "#F7F7F9", border: "none", borderRadius: 7, padding: "5px 10px", fontSize: 11, fontWeight: 600, color: "#0D1B3E", cursor: "pointer" },
  formCard: { background: "#fff", borderRadius: 14, padding: 16, marginBottom: 16, border: "1px solid #ECECEE" },
  formTitle: { margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: "#0D1B3E" },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #ECECEE", fontSize: 13, marginBottom: 10, boxSizing: "border-box", background: "#FAFAFA" },
  label: { display: "block", fontSize: 11, fontWeight: 600, color: "#9CA3AF", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  primaryBtn: { padding: "10px 14px", background: "#0D1B3E", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 2 },
  primaryBtnFull: { width: "100%", padding: 12, background: "#0D1B3E", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  secondaryBtn: { padding: "10px 14px", background: "#F7F7F9", color: "#374151", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", flex: 1 },
  financeGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 },
  financeCard: { background: "#fff", border: "1px solid #ECECEE", borderRadius: 14, padding: "14px 12px" },
  financeLabel: { margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.3 },
  financeValue: { margin: 0, fontSize: 20, fontWeight: 700, color: "#0D1B3E" },
  activityGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 },
};